import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import { useFilter } from "../../../../FilterContext";
import jsonData from "../../../../current-survey-numeric.json";

const calculateAverageScores = (data, categories) => {
  const scores = Object.keys(categories).reduce((acc, key) => {
    const categoryScores = categories[key].map((question) =>
      data
        .map((item) => item[question])
        .filter(
          (score) =>
            score !== undefined && score !== null && score >= 1 && score <= 5
        )
    );

    const categoryAverages = categoryScores.map((scoresArray) =>
      scoresArray.length
        ? scoresArray.reduce((a, b) => a + b, 0) / scoresArray.length
        : 0
    );

    acc[key] =
      categoryAverages.reduce((a, b) => a + b, 0) / categoryAverages.length;
    return acc;
  }, {});

  // Format scores to 1 decimal place
  Object.keys(scores).forEach((key) => {
    scores[key] = parseFloat(scores[key].toFixed(1));
  });

  return scores;
};

const D03BarCharts = () => {
  const { filterCriteria } = useFilter();
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [chartInstances, setChartInstances] = useState([]);

  // Get container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        setContainerHeight(height);
      }
    };

    // Initialize dimensions
    updateDimensions();

    // Set up resize listener
    window.addEventListener('resize', updateDimensions);

    // Clean up
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  useEffect(() => {
    // Destroy previous chart instances to prevent duplicates
    chartInstances.forEach(chart => chart.destroy());
    setChartInstances([]);

    const categories = {
      "Role Clarity: Clarity": ["MQ0044", "MQ0047", "MQ0017"],
      "Role Clarity: Resources": ["MQ0048", "MQ0051", "MQ0050"],
      "Role Clarity: Work Outcomes": ["MQ0095", "MQ0016", "MQ0059"],
      "Role Clarity: Feedback": ["MQ0104", "MQ0173", "MQ0054"],
      "Work Context: Safety": ["MQ0190", "MQ0141", "MQ0343"],
      "Work Context: Compensation": ["MQ0088", "MQ0090", "MQ0091"],
      "Work Context: Leadership": ["MQ0086", "MQ0034", "MQ0041"],
      "Work Context: Communication": ["MQ0194", "MQ0321", "MQ0058"],
      "Work Context: Supervision": ["MQ0056", "MQ0057", "MQ0038"],
      "Job Satisfaction: Recognition": ["MQ0053", "MQ0171", "MQ0103"],
      "Job Satisfaction: Autonomy": ["MQ0071", "MQ0202", "MQ0027"],
      "Job Satisfaction: Meaningfulness": ["MQ0023", "MQ0024", "MQ0026"],
      "Job Satisfaction: Growth": ["MQ0064", "MQ0062", "MQ0043"],
      "Job Satisfaction: Teamwork": ["MQ0340", "MQ0341", "MQ0094"],
      "Organizational Commitment: Belonging": ["MQ0021", "MQ0080", "MQ0302"],
      "Organizational Commitment: Vision and Values": [
        "MQ0072",
        "MQ0029",
        "MQ0012",
      ],
      "Organizational Commitment: Inclusion": ["MQ0311", "MQ0312", "MQ0331"],
      "Organizational Commitment: Openness": ["MQ0320", "MQ0046", "MQ0100"],
    };

    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every((key) => {
        const criteria = filterCriteria[key];
        if (Array.isArray(criteria)) {
          return criteria.length === 0 || criteria.includes(item[key]);
        }
        return criteria === "" || item[key] === criteria;
      });
    });

    const scores = calculateAverageScores(filteredData, categories);

    // Calculate chart heights based on container size
    const singleChartHeight = Math.max(100, Math.floor(containerHeight / 4) - 10);

    const options = {
      chart: {
        type: "bar",
        height: `${singleChartHeight}px`,
        fontFamily: 'Arial, sans-serif',
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
      },
      grid: {
        padding: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 45, // Increased right padding to make room for labels
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 1,
          borderRadiusApplication: "end",
          horizontal: true,
          distributed: false,
          dataLabels: {
            position: "right", // Position labels to the right of the bars
          },
          barHeight: "70%",
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val.toFixed(1);
        },
        offsetX: 45, // Move labels further away from bars
        textAnchor: "start", // Align text to start
        style: {
          colors: ["#000"],
          fontSize: '11px',
          fontWeight: 'normal',
        },
        background: {
          enabled: false,
        },
      },
    };

    const createChart = (element, data, categories, color) => {
      const chartElement = document.querySelector(element);
      if (!chartElement) return null;

      const optionsViz = {
        ...options,
        series: [
          {
            name: "Avg Score",
            data,
          },
        ],
        colors: [color],
        xaxis: {
          categories,
          max: 5.0,
          min: 0,
          tickAmount: 5,
          labels: {
            show: true,
            style: {
              fontSize: '10px',
            },
            formatter: function(val) {
              return val.toFixed(1);
            }
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: true,
            style: {
              fontSize: '10px',
            },
            maxWidth: 120,
          },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function(val) {
              return val.toFixed(1);
            }
          }
        }
      };

      const chart = new ApexCharts(chartElement, optionsViz);
      chart.render();
      return chart;
    };

    // Collect chart instances for cleanup
    const newChartInstances = [];

    // Create each chart only if the element exists
    const charts = [
      {
        id: "#rc-bar",
        title: "Role Clarity",
        data: [
          scores["Role Clarity: Clarity"],
          scores["Role Clarity: Feedback"],
          scores["Role Clarity: Resources"],
          scores["Role Clarity: Work Outcomes"],
        ],
        categories: ["Clarity", "Feedback", "Resources", "Work Outcomes"],
        color: "#004983"
      },
      {
        id: "#js-bar",
        title: "Job Satisfaction",
        data: [
          scores["Job Satisfaction: Autonomy"],
          scores["Job Satisfaction: Growth"],
          scores["Job Satisfaction: Meaningfulness"],
          scores["Job Satisfaction: Recognition"],
          scores["Job Satisfaction: Teamwork"],
        ],
        categories: ["Autonomy", "Growth", "Meaningfulness", "Recognition", "Teamwork"],
        color: "#008000"
      },
      {
        id: "#wc-bar",
        title: "Work Context",
        data: [
          scores["Work Context: Communication"],
          scores["Work Context: Compensation"],
          scores["Work Context: Leadership"],
          scores["Work Context: Safety"],
          scores["Work Context: Supervision"],
        ],
        categories: [
          "Communication",
          "Compensation",
          "Leadership",
          "Safety",
          "Supervision",
        ],
        color: "#ff8c00"
      },
      {
        id: "#oc-bar",
        title: "Organizational Commitment",
        data: [
          scores["Organizational Commitment: Belonging"],
          scores["Organizational Commitment: Inclusion"],
          scores["Organizational Commitment: Openness"],
          scores["Organizational Commitment: Vision and Values"],
        ],
        categories: ["Belonging", "Inclusion", "Openness", "Vision and Values"],
        color: "#ff0000"
      }
    ];

    // Clear any existing titles first to avoid duplicates
    document.querySelectorAll('.chart-title').forEach(el => el.remove());

    // Add title elements and create charts
    charts.forEach(chart => {
      const element = document.querySelector(chart.id);
      if (element) {
        // Add single title for each chart section
        const titleElement = document.createElement('div');
        titleElement.className = 'chart-title';
        titleElement.innerHTML = chart.title;
        titleElement.style.cssText = 'font-size: 12px; font-weight: bold; margin-top: 5px; margin-bottom: 2px; background-color: #808080; color: white; padding: 5px; text-align: center;';
        
        // Insert title before the chart
        element.parentNode.insertBefore(titleElement, element);
        
        // Create the chart
        const chartInstance = createChart(chart.id, chart.data, chart.categories, chart.color);
        if (chartInstance) {
          newChartInstances.push(chartInstance);
        }
      }
    });

    setChartInstances(newChartInstances);

    // Clean up the charts on component unmount
    return () => {
      newChartInstances.forEach((chart) => chart.destroy());
    };
  }, [filterCriteria, containerHeight]);

  // Chart container styling
  const chartContainerStyle = {
    height: '22%',
    marginBottom: '5px',
  };

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', color: "black" }}>
      <div id="rc-bar" style={chartContainerStyle}></div>
      <div id="js-bar" style={chartContainerStyle}></div>
      <div id="wc-bar" style={chartContainerStyle}></div>
      <div id="oc-bar" style={chartContainerStyle}></div>
    </div>
  );
};

export default D03BarCharts;